import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../api/axiosInstance';
import { UserPlus, User as UserIcon, Circle, ChevronDown, Check, Edit, Trash2, X, RefreshCcw } from 'lucide-react';
import { SCREEN_ACCESS_OPTIONS as SCREEN_ACCESS_OPTIONS_CONST, displayNameFor, USER_ROLES as USER_ROLES_CONST, UserRole } from '../constants/screenAccess';

// Define the component props
interface UserManagementProps { }

// Define the User and Form types for better type safety with TypeScript
interface User {
  id: number;
  guid: string;
  name: string;
  email: string;
  mobile: string | null;
  role: UserRole;
  isActive: boolean;
  activityStatus: string;
  screenAccess: string[] | null;
}

interface FormState {
  name: string;
  email: string; // separate email field
  mobile: string; // separate mobile field (10 digits)
  password: string;
  role: UserRole;
  isActive: boolean;
  screenAccess?: string[];
}

// Global event-based toast notification system
export function showGlobalToast(message: string, type: 'success' | 'error' | 'celebration' = 'success') {
  window.dispatchEvent(new CustomEvent('global-toast', { detail: { message, type } }));
}

const UserManagement: React.FC<UserManagementProps> = () => {
  const [form, setForm] = useState<FormState>({
    name: '',
    email: '',
    mobile: '',
    password: '',
    role: 'Manager',
    isActive: true, // Default to Active (boolean)
    screenAccess: [],
  });
  const [users, setUsers] = useState<User[]>([]);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState<boolean>(false);
  const [isScreenDropdownOpen, setIsScreenDropdownOpen] = useState<boolean>(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isFormVisible, setIsFormVisible] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const screenAccessRef = useRef<HTMLDivElement>(null);

  // Fetch all users when the component mounts
  useEffect(() => {
    fetchUsers();
  }, []);

  // Handle clicks outside the role dropdown to close it
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setIsStatusDropdownOpen(false);
      }
      if (screenAccessRef.current && !screenAccessRef.current.contains(event.target as Node)) {
        setIsScreenDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [dropdownRef, statusDropdownRef]);

  const fetchUsers = async (isManualRefresh = false) => {
    setIsLoading(true);
    try {
      const restaurantGuid = localStorage.getItem('restaurantGuid') || '';
      if (!restaurantGuid) {
        setError('Restaurant not found.');
        return;
      }
      const response = await axiosInstance.post('/auth/GetRestaurantAllUsers',);
      // The API now returns a boolean for isActive, so we can use it directly.
      setUsers(response.data);
      if (isManualRefresh) {
        showGlobalToast('User list refreshed successfully!', 'success');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleRoleSelect = (role: UserRole) => {
    setForm({ ...form, role });
    setIsDropdownOpen(false);
  };

  // toggle accepts the access KEY (e.g. 'orders', 'analytics') and stores keys in the form
  const toggleScreenAccess = (accessKey: string) => {
    setForm(prev => {
      const arr = prev.screenAccess || [];
      const exists = arr.includes(accessKey);
      const next = exists ? arr.filter(a => a !== accessKey) : [...arr, accessKey];
      return { ...prev, screenAccess: next };
    });
  };

  // Toggle all screen access keys on/off
  const toggleAllScreenAccess = () => {
    const allKeys = SCREEN_ACCESS_OPTIONS_CONST.map(o => o.key as string);
    const current = form.screenAccess || [];
    const allSelected = allKeys.every(k => current.includes(k));
    setForm(prev => ({ ...prev, screenAccess: allSelected ? [] : allKeys }));
  };

  const handleStatusSelect = (status: boolean) => {
    setForm({ ...form, isActive: status });
    setIsStatusDropdownOpen(false);
  };

  const validateEmail = (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const validateMobile = (value: string) => {
    const mobileRegex = /^\d{10}$/;
    return mobileRegex.test(value);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    // screenAccess may come as an array or as a CSV string from backend
    const rawAccess = (user as any).screenAccess;
    let parsedAccess: string[] = [];
    if (Array.isArray(rawAccess)) parsedAccess = rawAccess;
    else if (typeof rawAccess === 'string' && rawAccess.trim().length > 0) {
      parsedAccess = rawAccess.split(',').map(s => s.trim()).filter(Boolean);
    }

    setForm({
      name: user.name,
      email: user.email || '',
      mobile: user.mobile || '',
      password: '', // Password is not populated for security
      role: user.role as 'Manager' | 'Dining Associate',
      isActive: user.isActive,
      screenAccess: parsedAccess,
    });
    setIsFormVisible(true);
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setForm({
      name: '',
      email: '',
      mobile: '',
      password: '',
      role: 'Manager',
      isActive: true,
      screenAccess: [],
    });
    setIsFormVisible(false);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      const response = await axiosInstance.delete(`/auth/DeleteUser/${id}`);
      showGlobalToast(response.data?.message || 'User deleted successfully!', 'success');
      fetchUsers();
    } catch (err: any) {
      showGlobalToast(err.response?.data?.message || 'Failed to delete user', 'error');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Field validation checks
    if (!form.name.trim()) {
      setError('Name is mandatory.');
      return;
    }
    if (!form.email.trim() && !form.mobile.trim()) {
      setError('Email or Mobile Number is mandatory.');
      return;
    }

    // Password validation for new users AND updated users
    if (!form.password.trim()) {
      setError('Password is mandatory.');
      return;
    }
    if (form.password.trim().length < 6 || form.password.trim().length > 100) {
      setError('Password must be between 6 and 100 characters long.');
      return;
    }

    // Validate provided contact fields
    if (form.email.trim() && !validateEmail(form.email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    if (form.mobile.trim() && !validateMobile(form.mobile.trim())) {
      setError('Please enter a valid 10-digit mobile number.');
      return;
    }

    try {
      const restaurantGuid = localStorage.getItem('restaurantGuid') || '';
      if (!restaurantGuid) {
        setError('Restaurant not found.');
        return;
      }

      const payload = {
        name: form.name,
        email: form.email || '',
        mobile: form.mobile || '',
        password: form.password,
        role: form.role,
        isActive: form.isActive, // Directly use the boolean value
        // send screenAccess as CSV string expected by backend (or empty string)
        screenAccess: (form.screenAccess && form.screenAccess.length) ? (form.screenAccess as string[]).join(',') : '',
        restaurantGuid: restaurantGuid,
      };

      if (editingUser) {
        // Update user logic
        const updatePayload = {
          ...payload,
          id: editingUser.id
        }
        const response = await axiosInstance.put(`/auth/UpdateUser/${editingUser.id}`, updatePayload);
        setSuccess(response.data?.message || `User "${form.name}" updated successfully!`);
        setEditingUser(null);
      } else {
        // Create user logic
        const response = await axiosInstance.post('/auth/register', payload);
        setSuccess(response.data?.message || `${form.role} created successfully!`);
      }

      // Clear the form and hide it
      setForm({ name: '', email: '', mobile: '', password: '', role: 'Manager', isActive: true, screenAccess: [] });
      setIsFormVisible(false);
      fetchUsers(); // Refresh the user list
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to process user request');
    }
  };

  return (
    <div className="container mx-auto p-6 bg-gray-100 text-gray-900 dark:bg-gray-900 dark:text-gray-100 min-h-full overflow-auto">
      <h2 className="text-3xl font-bold mb-6">User Management</h2>
      <div className="flex flex-col gap-8">
        {/* Slide-over drawer for Add/Edit User Form */}
        {isFormVisible && (
          <div className="fixed inset-0 z-50 flex">
            {/* Overlay */}
            <button aria-label="Close panel" onClick={handleCancelEdit} className="absolute inset-0 bg-black bg-opacity-40 backdrop-blur-sm" />

            {/* Drawer */}
            <aside className="ml-auto w-full sm:w-96 md:w-[480px] h-full bg-white dark:bg-gray-800 shadow-2xl transform transition-transform duration-300 ease-in-out overflow-auto">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <button onClick={handleCancelEdit} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors" title="Close">
                    <X className="h-5 w-5" />
                  </button>
                  <h3 className="text-xl font-bold flex items-center gap-2 ml-auto">
                    <UserPlus className="text-orange-500" />
                    <span className="text-right">{editingUser ? 'Update User' : 'Add New User'}</span>
                  </h3>
                </div>

                {error && <div className="text-red-500 mb-4">{error}</div>}
                {success && <div className="text-green-600 mb-4">{success}</div>}

                <form onSubmit={handleSubmit} className="flex flex-col gap-4 pb-6">
                  <input
                    name="name"
                    placeholder="Name"
                    value={form.name}
                    onChange={handleChange}
                    className="p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-50 border-gray-300 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                    required
                  />
                  {/* Email and Mobile fields (at least one required) */}
                  <input
                    name="email"
                    type="email"
                    placeholder="Email (optional)"
                    value={form.email}
                    onChange={handleChange}
                    className="p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-50 border-gray-300 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                  />
                  <input
                    name="mobile"
                    type="text"
                    placeholder="Mobile Number (10 digits) (optional)"
                    value={form.mobile}
                    onChange={handleChange}
                    className="p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-50 border-gray-300 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                  />
                  <input
                    name="password"
                    type="password"
                    placeholder="Password"
                    value={form.password}
                    onChange={handleChange}
                    className="p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-50 border-gray-300 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                    required // Password is now required for both new and updated users
                  />

                  {/* Custom Dropdown for Role */}
                  <div className="relative" ref={dropdownRef}>
                    <div
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors bg-gray-50 border-gray-300 dark:bg-gray-700 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <span className="text-gray-900 dark:text-gray-100">{form.role}</span>
                      <ChevronDown className={`h-4 w-4 transform transition-transform ${isDropdownOpen ? 'rotate-180' : 'rotate-0'}`} />
                    </div>
                    {isDropdownOpen && (
                      <ul className="absolute z-10 w-full overflow-hidden rounded-lg shadow-xl border mt-2 bg-white border-gray-200 dark:bg-gray-700 dark:border-gray-600 max-h-48 overflow-y-auto">
                          {USER_ROLES_CONST.map((roleOption) => {
                            const isCurrentRole = roleOption === form.role;
                            return (
                              <li
                                key={roleOption}
                                onClick={() => handleRoleSelect(roleOption as UserRole)}
                                className={`flex items-center justify-between p-3 text-sm cursor-pointer transition-colors text-gray-800 hover:bg-orange-600 hover:text-white dark:text-gray-100 dark:hover:bg-orange-600 dark:hover:text-white ${isCurrentRole ? 'bg-gray-200 dark:bg-gray-600' : ''}`}
                              >
                                <span>{roleOption}</span>
                                {isCurrentRole && <Check className="h-4 w-4 text-orange-500" />}
                              </li>
                            );
                          })}
                        </ul>
                    )}
                  </div>

                  {/* Screen Access multi-select dropdown */}
                  <div className="mt-2 relative" ref={screenAccessRef}>
                    <div
                      onClick={() => setIsScreenDropdownOpen(s => !s)}
                      className="flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors bg-gray-50 border-gray-300 dark:bg-gray-700 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <span className="text-gray-900 dark:text-gray-100 w-full">
                        {(form.screenAccess && form.screenAccess.length > 0) ? (
                          <div className="flex flex-wrap gap-2 items-center">
                            {(form.screenAccess as string[]).map(k => (
                              <span
                                key={k}
                                className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 dark:bg-orange-700 dark:text-white"
                              >
                                {displayNameFor(k)}
                              </span>
                            ))}
                          </div>
                        ) : 'Select Screen Access'}
                      </span>
                      <ChevronDown className={`h-4 w-4 transform transition-transform ${isScreenDropdownOpen ? 'rotate-180' : 'rotate-0'}`} />
                    </div>
                    {isScreenDropdownOpen && (
                      <ul className="absolute z-10 w-full overflow-hidden rounded-lg shadow-xl border mt-2 bg-white border-gray-200 dark:bg-gray-700 dark:border-gray-600 max-h-60 overflow-y-auto">
                        {/* Select all / clear all control */}
                        <li className="p-2 border-b">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              className="h-4 w-4"
                              onChange={() => toggleAllScreenAccess()}
                              checked={SCREEN_ACCESS_OPTIONS_CONST.every(opt => (form.screenAccess || []).includes(opt.key as string))}
                            />
                            <span className="text-sm font-medium text-gray-800 dark:text-gray-100">Select all</span>
                          </label>
                        </li>
                        {SCREEN_ACCESS_OPTIONS_CONST.map(opt => {
                          const checked = (form.screenAccess || []).includes(opt.key as string);
                          return (
                            <li key={opt.key} className="p-2">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={checked} onChange={() => toggleScreenAccess(opt.key as string)} className="h-4 w-4" />
                                <span className="text-sm text-gray-800 dark:text-gray-100">{opt.label}</span>
                              </label>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>

                  {editingUser && (
                    <div className="relative" ref={statusDropdownRef}>
                      <div
                        onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
                        className="flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors bg-gray-50 border-gray-300 dark:bg-gray-700 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <span className="text-gray-900 dark:text-gray-100">{form.isActive ? 'Active' : 'Inactive'}</span>
                        <ChevronDown className={`h-4 w-4 transform transition-transform ${isStatusDropdownOpen ? 'rotate-180' : 'rotate-0'}`} />
                      </div>
                      {isStatusDropdownOpen && (
                        <ul className="absolute z-10 w-full overflow-hidden rounded-lg shadow-xl border mt-2 bg-white border-gray-200 dark:bg-gray-700 dark:border-gray-600">
                          <li
                            onClick={() => handleStatusSelect(true)}
                            className={`flex items-center justify-between p-3 text-sm cursor-pointer transition-colors text-gray-800 hover:bg-orange-600 hover:text-white dark:text-gray-100 dark:hover:bg-orange-600 dark:hover:text-white ${form.isActive ? 'bg-gray-200 dark:bg-gray-600' : ''}`}
                          >
                            <span>Active</span>
                            {form.isActive && <Check className="h-4 w-4 text-green-500" />}
                          </li>
                          <li
                            onClick={() => handleStatusSelect(false)}
                            className={`flex items-center justify-between p-3 text-sm cursor-pointer transition-colors text-gray-800 hover:bg-orange-600 hover:text-white dark:text-gray-100 dark:hover:bg-orange-600 dark:hover:text-white ${!form.isActive ? 'bg_gray-200 dark:bg-gray-600' : ''}`}
                          >
                            <span>Inactive</span>
                            {!form.isActive && <Check className="h-4 w-4 text-orange-500" />}
                          </li>
                        </ul>
                      )}
                    </div>
                  )}

                  <div className="flex gap-2 mt-2">
                    <button type="submit" className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-3 rounded-lg font-semibold transition duration-300 ease-in-out transform hover:scale-105">
                      {editingUser ? 'Update User' : 'Create User'}
                    </button>
                    {editingUser && (
                      <button type="button" onClick={handleCancelEdit} className="flex-1 bg-gray-400 hover:bg-gray-500 text-white py-3 rounded-lg font-semibold transition duration-300 ease-in-out transform hover:scale-105">
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </aside>
          </div>
        )}

        {/* User List Table */}
        <div className="p-6 rounded-2xl shadow-lg bg-white dark:bg-gray-800">
          <h3 className="text-xl font-bold mb-4 flex items-center gap-2 justify-between">
            <span className="flex items-center gap-2">
              <UserIcon className="text-blue-500" />
              All Users
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setIsFormVisible(true);
                  setEditingUser(null);
                }}
                className="flex items-center gap-1 text-sm bg-orange-600 hover:bg-orange-700 text-white font-semibold py-2 px-4 rounded-full transition duration-300 ease-in-out"
                title="Add New User"
              >
                <UserPlus className="h-4 w-4" />
                <span>Add</span>
              </button>
              <button
                onClick={() => fetchUsers(true)}
                className="text-gray-400 hover:text-orange-500 transition-colors"
                title="Refresh User List"
              >
                <RefreshCcw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </h3>
          {isLoading ? (
            <div className="text-center text-gray-500 dark:text-gray-400">Loading users...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Name</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Email</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Mobile</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Role</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Account Status</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Activity Status</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white dark:bg-gray-800 dark:divide-gray-700">
                  {users.length > 0 ? (
                    users.map((user) => (
                      <tr key={user.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">{user.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{user.email || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{user.mobile || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{user.role}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {user.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Circle className={`inline-block h-3 w-3 ${user.activityStatus === 'Online' ? 'text-green-500 fill-current' : 'text-red-500 fill-current'}`} />
                          <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">{user.activityStatus}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEdit(user)}
                              className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-200"
                              title="Edit User"
                            >
                              <Edit className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(user.id)}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-200"
                              title="Delete User"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">No users found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
